import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import { AskRichardWordmark, BRAND_COLORS } from '~/components/brand';

import { useSetReportingLeDataSessionVisibility } from '~/hooks/apis/reporting/useSetReportingLeDataSessionVisibility';

import type { LeDataSessionVisibility } from '~/types/leData';

import { getLeDataApiErrorMessage } from '~/utils/leData/getLeDataApiErrorMessage';

import { useDebugDetailsFlag } from './flags';

const EXPORT_WHILE_WORKING = 'Wait for the current answer to finish';

interface ThreadBarProps {
  title: string;
  compact?: boolean;
  actions?: React.ReactNode;
  leadingAction?: React.ReactNode;
  sessionId: string | null;
  visibility: LeDataSessionVisibility;
  onVisibilityChanged: (sessionId: string, visibility: LeDataSessionVisibility) => void;
  working: boolean;
  exporting: boolean;
  onExport: () => void;
}

function sharedSessionUrl(sessionId: string): string {
  return `${window.location.origin}/ask-richard/shared/${encodeURIComponent(sessionId)}`;
}

export default function ThreadBar({
  title,
  compact = false,
  actions,
  leadingAction,
  sessionId,
  visibility,
  onVisibilityChanged,
  working,
  exporting,
  onExport,
}: ThreadBarProps) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const debugDetails = useDebugDetailsFlag();
  const { mutateAsync: setSessionVisibility, isPending: visibilityPending } = useSetReportingLeDataSessionVisibility();
  const [visibilityAnchor, setVisibilityAnchor] = useState<HTMLElement | null>(null);
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);
  const shared = visibility === 'luxuryescapes_link';
  const visibilityLabel = shared ? 'LuxuryEscapes (anyone with link)' : 'Private';
  const canShare = Boolean(sessionId) && !working;

  const copy = async (text: string, success: string) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar(success, { variant: 'success' });
    } catch {
      enqueueSnackbar('Copying failed', { variant: 'warning' });
    }
  };

  const changeVisibility = async (next: LeDataSessionVisibility) => {
    setVisibilityAnchor(null);
    if (!sessionId) {
      return;
    }
    try {
      const updated = await setSessionVisibility({ sessionId, visibility: next });
      onVisibilityChanged(sessionId, updated.visibility ?? next);
    } catch (error) {
      enqueueSnackbar(getLeDataApiErrorMessage(error), { variant: 'error' });
      return;
    }
    if (next === 'luxuryescapes_link') {
      await copy(sharedSessionUrl(sessionId), 'Shared link copied to clipboard');
    } else {
      enqueueSnackbar('Conversation set to private', { variant: 'success' });
    }
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      data-testid="ui-stream-thread-bar"
      data-visibility={visibility}
      sx={{
        px: compact ? 1 : { xs: 1.5, md: 3 },
        minHeight: compact ? 44 : undefined,
        py: 0.75,
        borderBottom: compact ? 0 : 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {leadingAction}
      {!compact && (
        <Typography noWrap title={title} sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600 }}>
          {title}
        </Typography>
      )}
      {compact && (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0.5,
            pb: 0.5,
            borderBottom: 2,
            borderColor: theme.palette.mode === 'dark' ? BRAND_COLORS.compassWhite : BRAND_COLORS.oceanTeal,
          }}
        >
          <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
          <AskRichardWordmark size={14} inverted={theme.palette.mode === 'dark'} />
        </Box>
      )}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
        {canShare ? (
          <Tooltip title={`Share conversation · ${visibilityLabel}`}>
            <span>
              <IconButton
                size="small"
                aria-label="Share conversation"
                aria-haspopup="menu"
                disabled={visibilityPending}
                onClick={(event) => setVisibilityAnchor(event.currentTarget)}
              >
                <ShareOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
        <Menu anchorEl={visibilityAnchor} open={Boolean(visibilityAnchor)} onClose={() => setVisibilityAnchor(null)}>
          <MenuItem selected={!shared} onClick={() => void changeVisibility('private')}>
            <ListItemIcon>
              <LockOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Private" />
          </MenuItem>
          <MenuItem selected={shared} onClick={() => void changeVisibility('luxuryescapes_link')}>
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="LuxuryEscapes (anyone with link)" />
          </MenuItem>
          {shared && sessionId ? (
            <MenuItem
              onClick={() => {
                setVisibilityAnchor(null);
                void copy(sharedSessionUrl(sessionId), 'Shared link copied to clipboard');
              }}
            >
              <ListItemIcon>
                <ContentCopyOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Copy link" />
            </MenuItem>
          ) : null}
        </Menu>
        <Tooltip title={working ? EXPORT_WHILE_WORKING : 'Export conversation as PDF'}>
          <span>
            <IconButton
              size="small"
              aria-label={exporting ? 'Exporting conversation' : 'Export conversation as PDF'}
              disabled={exporting || working || (compact && !sessionId)}
              onClick={onExport}
            >
              {exporting ? <CircularProgress size={16} /> : <FileDownloadIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {actions}
        {debugDetails && (
          <>
            <Tooltip title="Conversation options">
              <IconButton
                size="small"
                aria-label="Conversation options"
                aria-haspopup="menu"
                onClick={(event) => setOptionsAnchor(event.currentTarget)}
              >
                <MoreHorizIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={optionsAnchor} open={Boolean(optionsAnchor)} onClose={() => setOptionsAnchor(null)}>
              <MenuItem
                disabled={!sessionId}
                onClick={() => {
                  setOptionsAnchor(null);
                  if (sessionId) {
                    void copy(sessionId, 'Session ID copied');
                  }
                }}
              >
                <ListItemIcon>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Copy session ID" />
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>
    </Stack>
  );
}
